import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, MapPin, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const positions = [
  { id: 1, title: 'Senior Full-Stack Developer', location: 'Remote / Hybrid', type: 'Full-Time' },
  { id: 2, title: 'UI/UX Lead Designer', location: 'New York, NY', type: 'Full-Time' },
  { id: 3, title: 'Cloud Solutions Architect', location: 'Remote', type: 'Contract' }
];

const Hiring = () => {
  return (
    <section className="py-24 relative overflow-hidden bg-transparent">
       <div className="container mx-auto px-6 max-w-7xl relative z-10">
          
          <div className="bg-slate-900 rounded-[3rem] p-8 md:p-14 lg:p-20 relative overflow-hidden shadow-[0_20px_50px_rgb(15,23,42,0.2)]">
             {/* Ethereal Background Glowing Orbs */}
             <div className="absolute top-0 right-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-blue-600/30 blur-[140px] rounded-full mix-blend-screen pointer-events-none -translate-y-1/3 translate-x-1/3"></div>
             <div className="absolute bottom-0 left-0 w-[300px] h-[300px] md:w-[450px] md:h-[450px] bg-indigo-600/30 blur-[120px] rounded-full mix-blend-screen pointer-events-none translate-y-1/3 -translate-x-1/3"></div>

             <div className="flex flex-col lg:flex-row gap-16 relative z-10">
                {/* Left Informational Area */}
                <div className="lg:w-1/2">
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 font-semibold text-sm mb-8"
                    >
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                      We're Hiring
                    </motion.div>
                    
                    <motion.h2 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
                    >
                      Join the team building the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">future</span>.
                    </motion.h2>
                    
                    <motion.p 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="text-slate-300 text-lg md:text-xl leading-relaxed mb-10 font-light"
                    >
                      We are always on the lookout for passionate innovators, brilliant engineers, and visionary creatives to join our rapidly growing family. Stop working jobs, start creating impact.
                    </motion.p>
                    
                    <Link to="/careers">
                      <motion.button 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-[0_0_20px_rgb(37,99,235,0.4)] hover:shadow-[0_0_30px_rgb(37,99,235,0.6)] group w-full sm:w-auto justify-center cursor-pointer"
                      >
                         View All Openings
                         <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </Link>
                </div>

                {/* Right Open Job Listings */}
                <div className="lg:w-1/2 flex flex-col gap-4">
                   {positions.map((job, idx) => (
                      <motion.div 
                        key={job.id}
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.15 }}
                        className="group bg-slate-800/40 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 backdrop-blur-md rounded-2xl p-6 md:p-8 transition-all cursor-pointer flex items-center justify-between shadow-sm hover:shadow-xl hover:-translate-y-1"
                      >
                         <div>
                            <h4 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">{job.title}</h4>
                            <div className="flex flex-wrap gap-4 text-slate-400 text-sm font-medium">
                               <span className="flex items-center gap-1.5 bg-slate-900/50 px-3 py-1.5 rounded-lg"><MapPin size={16} className="text-indigo-400"/> {job.location}</span>
                               <span className="flex items-center gap-1.5 bg-slate-900/50 px-3 py-1.5 rounded-lg"><Clock size={16} className="text-blue-400"/> {job.type}</span>
                            </div>
                         </div>
                         <div className="w-14 h-14 rounded-full bg-slate-700/50 flex items-center justify-center text-white group-hover:bg-blue-600 group-hover:shadow-[0_0_20px_rgb(37,99,235,0.5)] transition-all shrink-0">
                            <Briefcase size={24} />
                         </div>
                      </motion.div>
                   ))}
                   
                   <motion.div 
                     initial={{ opacity: 0 }}
                     whileInView={{ opacity: 1 }}
                     viewport={{ once: true }}
                     transition={{ delay: 0.6 }}
                     className="text-center mt-6 text-slate-400 text-sm font-medium cursor-pointer hover:text-white transition-colors"
                   >
                      Don't see a perfect fit? Send us a <span className="underline decoration-slate-500 underline-offset-4">speculative application</span>!
                   </motion.div>
                </div>
             </div>
          </div>
       </div>
    </section>
  );
};

export default Hiring;
